<template>
  <div class="onboarding-summary">
    <!-- Left selection : steps -->
    <OnboardingSteps
      :current-step="'summary'"
    />

    <!-- Right section : summary table -->
    <div class="onboarding-content">
      <!-- Title section -->
      <div class="onboarding-header">
        <div class="onboarding-header-content">
          <h1 class="onboarding-header-title">
            {{ $t('onboarding.summary.title', { count: totalConnectedPages }) }}
          </h1>
          <p class="onboarding-header-subtitle">
            {{ $t('onboarding.summary.subtitle') }}
          </p>
        </div>
      </div>

      <!-- Quota warning if exceeded -->
      <div class="onboarding-quota-warning" v-if="hasExceededPageQuota">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div>
          <strong>{{ $t('onboarding.connect_pages.quota_status') }}: {{ totalConnectedPages }}/{{ userPlanPageQuota }}</strong>
          <p>
            {{ $t('onboarding.connect_pages.quota_exceeded') }}
            {{ $t('onboarding.summary.quota_action_delete') }}
          </p>
          <p class="quota-info">{{ $t('onboarding.connect_pages.quota_upgrade_info') }}</p>
        </div>
      </div>

      <!-- Search bar -->
      <div class="onboarding-search-bar">
        <svg class="search-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="6" stroke="#9CA3AF" stroke-width="2"/>
          <path d="M12.5 12.5L16 16" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <input
          type="text"
          v-model="searchQuery"
          :placeholder="$t('onboarding.summary.search_placeholder')"
          class="search-input"
        />
      </div>

      <!-- Summary table -->
      <div class="onboarding-table-container">
        <table class="onboarding-table">
          <thead>
            <tr>
              <!-- <th class="col-checkbox">
                <input
                  type="checkbox"
                  v-model="selectAll"
                  @change="toggleSelectAll"
                />
              </th> -->
              <th class="col-avatar"></th>
              <th class="col-name" @click="sortBy('name')">
                {{ $t('onboarding.summary.table_header_name') }}
                <span class="sort-indicator" v-if="sortColumn === 'name'">
                  {{ sortDirection === 'asc' ? '▲' : '▼' }}
                </span>
              </th>
              <th class="col-network" @click="sortBy('network')">
                {{ $t('onboarding.summary.table_header_network') }}
                <span class="sort-indicator" v-if="sortColumn === 'network'">
                  {{ sortDirection === 'asc' ? '▲' : '▼' }}
                </span>
              </th>
              <!-- <th class="col-group" @click="sortBy('group')">
                {{ $t('onboarding.summary.table_header_group') }}
                <span class="sort-indicator" v-if="sortColumn === 'group'">
                  {{ sortDirection === 'asc' ? '▲' : '▼' }}
                </span>
              </th> -->
              <th class="col-status" @click="sortBy('APIStatus')">
                <!-- <img src="../assets/icons/logout-icon.svg" alt="API" class="status-header-icon" title="Statut API" /> -->
                <span class="sort-indicator" v-if="sortColumn === 'APIStatus'">
                  {{ sortDirection === 'asc' ? '▲' : '▼' }}
                </span>
                {{ $t('onboarding.summary.table_header_api_status') }}
              </th>
              <th class="col-status" @click="sortBy('nativeStatus')">
                <!-- <img src="../assets/icons/native-icon2.svg" alt="Native" class="status-header-icon" title="Statut Native" /> -->
                <span class="sort-indicator" v-if="sortColumn === 'nativeStatus'">
                  {{ sortDirection === 'asc' ? '▲' : '▼' }}
                </span>
                {{ $t('onboarding.summary.table_header_native_status') }}
              </th>
              <th class="col-actions">{{ $t('onboarding.summary.table_header_actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="page in filteredPages"
              :key="`${page.network}-${page.id}`"
              class="table-row"
            >
              <!-- <td class="col-checkbox">
                <input
                  type="checkbox"
                  v-model="selectedPageIds"
                  :value="`${page.network}-${page.id}`"
                />
              </td> -->
              <td class="col-avatar">
                <img
                  v-if="page.avatar && !imageLoadErrors[page.id]"
                  :src="page.avatar"
                  :alt="page.name"
                  class="page-avatar"
                  @error="handleImageError(page.id)"
                />
                <div
                  v-else
                  class="page-avatar avatar-placeholder"
                >
                  {{ page.name.charAt(0).toUpperCase() }}
                </div>
              </td>
              <td class="col-name">{{ page.name }}</td>
              <td class="col-network">
                <div class="network-badge">
                  <img :src="getNetworkIcon(page.network)" :alt="page.network" class="network-icon" />
                  <span>{{ getNetworkName(page.network) }}</span>
                </div>
              </td>
              <!-- <td class="col-group">
                <span class="group-empty">—</span>
              </td> -->
              <td class="col-status">
                <span
                  class="status-badge"
                  :class="page.apiConnected ? 'connected' : 'not-connected'"
                >
                  <span class="status-dot"></span>
                  {{ page.apiConnected ? $t('onboarding.summary.status_connected') : $t('onboarding.summary.status_not_connected') }}
                </span>
              </td>
              <td class="col-status">
                <span v-if="!supportsNativeConnection(page.network)" class="group-empty">—</span>
                <span
                  v-else-if="page.nativeConnected"
                  class="status-badge connected"
                >
                  <span class="status-dot"></span>
                  {{ $t('onboarding.summary.status_connected') }}
                </span>
                <a
                  v-else-if="isNetworkNativeFeatureSelected(page.network)"
                  href="javascript:void(0)"
                  class="status-badge not-connected is-clickable"
                  @click="goToNativeConnection(page)"
                >
                  <span class="status-dot"></span>
                  {{ $t('onboarding.summary.status_not_connected') }}
                </a>
                <span
                  v-else
                  class="status-badge not-desired"
                >
                  <span class="status-dot"></span>
                  {{ $t('onboarding.summary.status_not_desired') }}
                </span>
              </td>
              <td class="col-actions">
                <button 
                  class="delete-btn" 
                  :title="$t('onboarding.summary.delete_page_title')"
                  @click="removePageFromOnboarding(page)"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2 4h12M5.5 4V2.5A1.5 1.5 0 0 1 7 1h2a1.5 1.5 0 0 1 1.5 1.5V4m2 0v9.5A1.5 1.5 0 0 1 11 15H5a1.5 1.5 0 0 1-1.5-1.5V4h9Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty state -->
        <div v-if="filteredPages.length === 0" class="empty-state">
          <p>{{ $t('onboarding.summary.empty_state') }}</p>
        </div>
      </div>

      <!-- Footer with navigation -->
      <div class="onboarding-footer-container">
        <div class="onboarding-footer">
          <button
            class="onboarding-btn-primary"
            :disabled="!canFinish || isFinishing"
            @click="finishOnboarding"
          >
            <span v-if="isFinishing" class="btn-spinner"></span>
            <span>{{ $t('onboarding.summary.finish_button') }}</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Loading spinner on finish -->
    <LoadingSpinner
      :show="isFinishing"
      :message="$t('onboarding.summary.finalizing')"
      hide-percent
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import OnboardingSteps from '../shared/OnboardingSteps.vue';
import LoadingSpinner from '../shared/LoadingSpinner.vue';
import Pages from '@/components/mixins/Pages.vue';
import User from '@/components/mixins/User.vue';
import Notif from '@/components/mixins/Notif.vue';
import { persistOnboardingPages, restoreOnboardingPages, clearOnboardingFlags } from '../utils/onboardingPersistence';
import { getNetworkName as _getNetworkName, getNetworkIcon } from '../utils/networkMaps';
import Lock from '@/utils/Lock.js';
import { quotaOverride } from '@/utils/quotas'

export default {
  name: 'StepSummary',

  components: {
    OnboardingSteps,
    LoadingSpinner
  },

  mixins: [Pages, User, Notif],

  data() {
    return {
      searchQuery: '',
      selectAll: false,
      imageLoadErrors: {},
      sortColumn: 'name',
      sortDirection: 'asc',
      isFinishing: false
    }
  },

  computed: {
    ...mapState('onboarding', [
      'connectedPages',
      'preExistingPageIds',
      'selectedPageIds',
      'nativeConnections',
      'selectedFeatures'
    ]),

    // Flatten all connected pages with network info
    // ONLY show pages that are:
    // 1. Selected (in selectedPageIds for their network) OR
    // 2. Pre-existing (in preExistingPageIds for their network)
    allPages() {
      const pages = []
      
      Object.keys(this.connectedPages).forEach(network => {
        const networkPages = this.connectedPages[network] || []
        const selectedIds = this.selectedPageIds[network] || []
        
        networkPages.forEach(page => {
          // Check by both id AND network_id to handle UUID vs network_id mismatches
          // (pages added with network_id fallback when newPage() returns 422)
          const isSelected = selectedIds.includes(page.id) ||
            (page.network_id && selectedIds.includes(page.network_id))
          
          if (isSelected) {
            pages.push({
              ...page,
              network: network,
              apiConnected: this.isApiConnected(page, network),
              nativeConnected: this.isNativeConnected(page)
            })
          }
        })
      })
      
      return pages
    },

    // Override the store's totalConnectedPages to use the unified page counting formula
    totalConnectedPages() {
      let count = 0
      const selectedNetworks = this.$store.state.onboarding.selectedNetworks || []
      const preExistingPageIds = this.preExistingPageIds || {}
      const selectedPageIds = this.selectedPageIds || {}
      
      const allNetworks = new Set([
        ...Object.keys(preExistingPageIds),
        ...Object.keys(selectedPageIds)
      ])
      
      allNetworks.forEach(network => {
        if (selectedNetworks.includes(network)) {
          count += (selectedPageIds[network] || []).length
        } else {
          count += (preExistingPageIds[network] || []).length
        }
      })
      return count
    },

    // Filter pages based on search query
    filteredPages() {
      let pages = this.allPages

      // Search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase()
        const connectedText = this.$t('onboarding.summary.search_connected').toLowerCase()
        const notConnectedText = this.$t('onboarding.summary.search_not_connected').toLowerCase()
        const notDesiredText = (this.$t('onboarding.summary.search_not_desired') || '').toLowerCase()
        pages = pages.filter(page => {
          const nativeFeatureSelected = this.isNetworkNativeFeatureSelected(page.network)
          const nativeMatchesNotConnected = !page.nativeConnected && nativeFeatureSelected && notConnectedText.includes(query)
          const nativeMatchesNotDesired = !page.nativeConnected && !nativeFeatureSelected && notDesiredText.includes(query)
          return (
            page.name.toLowerCase().includes(query) ||
            this.getNetworkName(page.network).toLowerCase().includes(query) ||
            (page.apiConnected && connectedText.includes(query)) ||
            (!page.apiConnected && notConnectedText.includes(query)) ||
            (page.nativeConnected && connectedText.includes(query)) ||
            nativeMatchesNotConnected ||
            nativeMatchesNotDesired
          )
        })
      }

      // Sort
      pages.sort((a, b) => {
        let aVal = a[this.sortColumn]
        let bVal = b[this.sortColumn]

        if (this.sortColumn === 'network') {
          aVal = this.getNetworkName(a.network)
          bVal = this.getNetworkName(b.network)
        } else if (this.sortColumn === 'group') {
          // Group column always shows "—", so sort by network name as fallback
          aVal = this.getNetworkName(a.network)
          bVal = this.getNetworkName(b.network)
        } else if (this.sortColumn === 'APIStatus') {
          // Sort by API connection status (boolean)
          aVal = a.apiConnected ? 1 : 0
          bVal = b.apiConnected ? 1 : 0
        } else if (this.sortColumn === 'nativeStatus') {
          // Special sorting: "—" (no support) ALWAYS at the end, regardless of sort direction
          const aSupport = this.supportsNativeConnection(a.network)
          const bSupport = this.supportsNativeConnection(b.network)
          
          if (!aSupport && !bSupport) {
            return 0 // Both have no support, keep same order
          } else if (!aSupport) {
            return 1 // a has no support, should always be after b
          } else if (!bSupport) {
            return -1 // b has no support, should always be after a
          } else {
            // Both support native connection, sort by connection status:
            // 2: Connected, 1: Not connected (desired), 0: Not desired
            const aDesired = this.isNetworkNativeFeatureSelected(a.network)
            const bDesired = this.isNetworkNativeFeatureSelected(b.network)
            aVal = a.nativeConnected ? 2 : (aDesired ? 1 : 0)
            bVal = b.nativeConnected ? 2 : (bDesired ? 1 : 0)
          }
        }

        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase()
          bVal = bVal.toLowerCase()
        }

        if (this.sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1
        } else {
          return aVal < bVal ? 1 : -1
        }
      })

      return pages
    },

    // Calculate user's page quota based on their plan
    userPlanPageQuota() {
      // If user profile is not loaded yet, do not restrict quota to avoid race conditions
      if (this.user === undefined) return 999
      // Quota personnalisé depuis l'admin, prioritaire sur le plan
      if (quotaOverride('pages', this.user) !== null) return quotaOverride('pages', this.user)
      const plan = this.getUserPlan || 0
      const quotas = Lock.rs_limit
      return quotas[plan] || 10
    },

    // Check if quota is exceeded
    hasExceededPageQuota() {
      return this.totalConnectedPages > this.userPlanPageQuota
    },

    // Check if can finish onboarding
    canFinish() {
      return !this.hasExceededPageQuota
    }
  },

  methods: {
    supportsNativeConnection(network) {
      const nativeNetworks = ['facebook', 'instagram', 'linkedin', 'tiktok', 'twitter', 'x']
      return nativeNetworks.includes(network)
    },

    isApiConnected(page, network) {
      // API connected = page has been connected via OAuth (default connection)
      // For now, consider all pages as API connected if they exist in connectedPages
      return true
    },

    isNativeConnected(page) {
      // Native connected = page has native/conciergerie connection
      // Check nativeConnections store which is updated during onboarding flow
      // Fallback to story_config.created for pages that existed before onboarding
      const nativeConnectionStatus = this.nativeConnections[page.id]
      if (nativeConnectionStatus) {
        return nativeConnectionStatus.connected === true
      }
      
      // Fallback: check story_config for pages loaded from backend
      return page.story_config && page.story_config.created === true
    },

    isNetworkNativeFeatureSelected(network) {
      if (!network) return false
      const selectedFeatures = this.selectedFeatures || this.$store.state.onboarding.selectedFeatures || []
      const key = network === 'x' ? 'twitter.native' : `${network}.native`
      return selectedFeatures.includes(key) || (network === 'twitter' && selectedFeatures.includes('x.native'))
    },

    goToNativeConnection(page) {
      if (!this.isNetworkNativeFeatureSelected(page.network)) {
        return
      }
      let targetNetwork = page.network
      const selectedNetworks = this.$store.state.onboarding.selectedNetworks || []
      if (!selectedNetworks.includes(targetNetwork)) {
        if (targetNetwork === 'twitter' && selectedNetworks.includes('x')) {
          targetNetwork = 'x'
        } else if (targetNetwork === 'x' && selectedNetworks.includes('twitter')) {
          targetNetwork = 'twitter'
        }
      }
      console.log('[StepSummary] Navigating to native connection for:', targetNetwork)
      this.$router.push({
        name: 'onboarding-connectPages',
        params: {
          network: targetNetwork,
          viewMode: 'native'
        }
      })
    },

    /**
     * Restore onboarding state from localStorage
     */
    restoreOnboardingState() {
      console.log('[StepSummary] Trying to restore from localStorage keys')
      const restored = restoreOnboardingPages(this.$store)
      if (restored) {
        console.log('[StepSummary] Successfully restored onboarding state from localStorage')
      } else {
        console.log('[StepSummary] No persisted state found')
      }
      return restored
    },

    getNetworkIcon(network) {
      return getNetworkIcon(network)
    },

    getNetworkName(network) {
      if (network === 'google') return 'Google Business Profile'
      return _getNetworkName(network)
    },

    handleImageError(id) {
      this.imageLoadErrors = { ...this.imageLoadErrors, [id]: true }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedPageIds = this.filteredPages.map(p => `${p.network}-${p.id}`)
      } else {
        this.selectedPageIds = []
      }
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },

    async removePageFromOnboarding(page) {
      console.log('[StepSummary] Deleting page:', page.name, page.id)
      
      try {
        // Call deletePage API from Pages mixin
        await this.deletePage(page.id)
        
        // Remove from store (also cleans up selectedPageIds, newlyConnectedPageIds, nativeConnections)
        this.$store.commit('onboarding/REMOVE_CONNECTED_PAGE', {
          network: page.network,
          pageId: page.id
        })
        
        // Persist changes to localStorage to prevent page from reappearing
        // when navigating back (restoreOnboardingPages() would otherwise restore old data)
        persistOnboardingPages(this.$store)
        console.log('[StepSummary] Page deletion persisted to localStorage')
        
        this.notify({
          title: this.$t('onboarding.summary.page_deleted_title'),
          text: this.$t('onboarding.summary.page_deleted_message', { name: page.name }),
          color: 'success',
          time: 3000
        })
      } catch (error) {
        console.error('[StepSummary] Error deleting page:', error)
        this.notify({
          title: this.$t('onboarding.summary.page_delete_error_title'),
          text: this.$t('onboarding.summary.page_delete_error_message'),
          color: 'danger',
          time: 5000
        })
      }
    },

    async finishOnboarding() {
      console.log('[StepSummary] Finishing onboarding')
      
      // Block if quota exceeded
      if (this.hasExceededPageQuota) {
        this.notify({
          title: `${this.$t('onboarding.connect_pages.quota_status')}: ${this.totalConnectedPages}/${this.userPlanPageQuota}`,
          text: this.$t('onboarding.summary.quota_exceeded_delete'),
          color: 'warning',
          time: 7000
        })
        return
      }
      
      this.isFinishing = true
      
      try {
        // Disconnect unselected pages before finishing (fast parallel safety net)
        await this.disconnectUnselectedPages()
        
        // Mark onboarding as completed (keep all user choices: networks, features, connected pages)
        // Unlike abandon/skip, finishing preserves the user's selections
        this.$store.commit('onboarding/COMPLETE_ONBOARDING')
        this.$store.commit('onboarding/SET_IS_ONBOARDING', false)
        
        // Clear temporary onboarding localStorage flags (mode, indices, etc.)
        this.clearOnboardingFlags()
        
        // Mark onboarding as completed in localStorage (prevents redirect loop)
        localStorage.setItem('onboardingCompleted', 'true')
        
        // Suggest guided tour for planner (entry point of the full tour)
        // localStorage.setItem('tour_prompt_pending', 'planner')
        
        console.log('[StepSummary] Onboarding completed, redirecting to dashboard')
        // Redirect to dashboard
        this.$router.push({ name: 'dash' })
      } catch (error) {
        console.error('[StepSummary] Error during finishOnboarding:', error)
      } finally {
        this.isFinishing = false
      }
    },

    async disconnectUnselectedPages() {
      console.log('[StepSummary] Checking for unselected pages to disconnect...')
      
      try {
        // Refresh pages from backend to get the latest state
        const response = await this.getPages()
        
        if (!response.ok || !response.body || !response.body.pages) {
          console.warn('[StepSummary] Could not fetch pages from backend')
          return
        }
        
        const allBackendPages = response.body.pages
        const selectedNetworks = this.$store.state.onboarding.selectedNetworks || []
        
        console.log('[StepSummary] Total backend pages:', allBackendPages.length)
        console.log('[StepSummary] Selected networks:', selectedNetworks)
        
        // Filter pages that are in selected networks but NOT selected in onboarding
        const pagesToDisconnect = []
        
        allBackendPages.forEach(page => {
          // Only consider pages from networks that were part of onboarding
          if (!selectedNetworks.includes(page.network)) {
            return
          }
          
          const selectedIds = this.selectedPageIds[page.network] || []
          
          // Check by id or network_id if page is selected
          const isSelected = selectedIds.includes(page.id) ||
            (page.network_id && selectedIds.includes(page.network_id))
          
          // Disconnect from backend if page belongs to a configured network but was NOT selected
          if (!isSelected) {
            console.log(`[StepSummary] Will disconnect unselected page from configured network ${page.network}: ${page.name} (${page.id})`)
            pagesToDisconnect.push(page)
          }
        })
        
        if (pagesToDisconnect.length === 0) {
          console.log('[StepSummary] No unselected pages to disconnect')
          return
        }
        
        console.log(`[StepSummary] Disconnecting ${pagesToDisconnect.length} unselected pages in parallel chunks...`)
        
        // Disconnect pages from backend in parallel chunks of 5
        for (let i = 0; i < pagesToDisconnect.length; i += 5) {
          const chunk = pagesToDisconnect.slice(i, i + 5)
          await Promise.allSettled(chunk.map(async (page) => {
            try {
              console.log(`[StepSummary] Disconnecting: ${page.name} (${page.id})`)
              await this.deletePage(page.id)
              console.log(`[StepSummary] Successfully disconnected: ${page.name}`)
            } catch (error) {
              console.error(`[StepSummary] Error disconnecting ${page.name}:`, error)
            }
          }))
        }
        
        console.log('[StepSummary] Finished disconnecting unselected pages')
      } catch (error) {
        console.error('[StepSummary] Error in disconnectUnselectedPages:', error)
      }
    },

    clearOnboardingFlags() {
      clearOnboardingFlags()
    },

    /**
     * Sync onboarding store with backend pages.
     * Ensures all pages connected during onboarding (and saved to backend via newPage)
     * are present in the onboarding store's connectedPages and selectedPageIds.
     * This prevents pages from being "lost" when navigating between onboarding steps.
     */
    async syncPagesFromBackend() {
      try {
        const response = await this.getPages()
        if (!response.ok || !response.body || !response.body.pages) {
          console.warn('[StepSummary] Could not fetch pages for backend sync')
          return
        }

        const allBackendPages = response.body.pages

        // Get selectedNetworks from store, fallback to localStorage if store is empty
        let selectedNetworks = this.$store.state.onboarding.selectedNetworks
        if (!selectedNetworks || selectedNetworks.length === 0) {
          const savedNetworks = localStorage.getItem('onboarding_selected_networks')
          if (savedNetworks) {
            try {
              selectedNetworks = JSON.parse(savedNetworks)
              console.log('[StepSummary] Restored selectedNetworks from localStorage:', selectedNetworks)
              this.$store.commit('onboarding/SET_NETWORKS', selectedNetworks)
            } catch (e) {
              console.warn('[StepSummary] Could not parse onboarding_selected_networks from localStorage')
            }
          }
        }

        if (!selectedNetworks || selectedNetworks.length === 0) {
          console.warn('[StepSummary] No selected networks found, skipping backend sync')
          return
        }

        console.log('[StepSummary] Syncing pages from backend. Total backend pages:', allBackendPages.length, 'Selected networks:', selectedNetworks)

        selectedNetworks.forEach(network => {
          const backendPages = allBackendPages.filter(p => p.network === network)
          if (backendPages.length === 0) return

          const currentConnected = this.connectedPages[network] || []

          backendPages.forEach(page => {
            // Check if this backend page exists in onboarding store (by id or network_id)
            const existingPage = currentConnected.find(p =>
              p.id === page.id || (p.network_id && p.network_id === page.network_id)
            )

            if (!existingPage) {
              console.log(`[StepSummary] Adding missing backend page to store: ${page.name} (${page.id}) for ${network}`)
              this.$store.commit('onboarding/ADD_CONNECTED_PAGE', {
                network,
                page: {
                  id: page.id,
                  network_id: page.network_id,
                  name: page.name,
                  username: page.username || page.name,
                  avatar: page.avatar,
                  story_config: page.story_config || null,
                  hasUuid: true
                }
              })
            } else if (existingPage.id !== page.id) {
              // Page exists but with a different id (network_id fallback vs backend UUID).
              // Update to the authoritative backend UUID to prevent ID mismatches
              // that cause pages to be invisible in the summary table.
              console.log(`[StepSummary] Updating page ID for ${page.name}: ${existingPage.id} → ${page.id}`)

              // Replace old id in selectedPageIds with backend UUID
              const currentSelectedIds = this.$store.state.onboarding.selectedPageIds[network] || []
              const oldIdIndex = currentSelectedIds.indexOf(existingPage.id)
              if (oldIdIndex !== -1) {
                const updatedIds = [...currentSelectedIds]
                updatedIds[oldIdIndex] = page.id
                this.$store.commit('onboarding/SET_SELECTED_PAGE_IDS', {
                  network,
                  pageIds: [...new Set(updatedIds)]
                })
              }

              // Update the page object's id in connectedPages
              this.$store.commit('onboarding/UPDATE_PAGE', {
                network,
                pageId: existingPage.id,
                updates: { id: page.id }
              })
            }

          })
        })

        // Deduplicate connectedPages for each network after sync.
        // Duplicates occur when a pre-existing page (UUID id) is reconnected during
        // onboarding and added with network_id as id. Both entries persist in localStorage.
        selectedNetworks.forEach(network => {
          const currentPages = this.connectedPages[network] || []
          if (currentPages.length <= 1) return

          const uniqueMap = new Map()
          currentPages.forEach(page => {
            const key = page.network_id || page.name || page.id
            if (!key) return

            if (uniqueMap.has(key)) {
              const existing = uniqueMap.get(key)
              // Prefer the entry with a UUID-format id
              const isUuid = (id) => typeof id === 'string' && /^[0-9a-f]{8}-/.test(id)
              if (isUuid(page.id) && !isUuid(existing.id)) {
                uniqueMap.set(key, page)
              }
            } else {
              uniqueMap.set(key, page)
            }
          })

          const deduped = Array.from(uniqueMap.values())
          if (deduped.length < currentPages.length) {
            console.log(`[StepSummary] Deduplicated ${network}: ${currentPages.length} → ${deduped.length} pages`)
            this.$store.commit('onboarding/SET_NETWORK_PAGES', { network, pages: deduped })
          }
        })

        console.log('[StepSummary] Backend sync complete')
      } catch (error) {
        console.error('[StepSummary] Error syncing pages from backend:', error)
      }
    }
  },

  beforeDestroy() {
    // Clean up if needed
  },

  async mounted() {
    console.log('=== [StepSummary] MOUNTED ===')

    // Ensure onboarding_mode flag is set (required for OAuth refresh redirects)
    localStorage.setItem('onboarding_mode', 'true')

    // If onboarding store is empty, try to restore from persisted state
    if (Object.keys(this.connectedPages).every(network => this.connectedPages[network].length === 0)) {
      console.log('[StepSummary] Onboarding store is empty, attempting to restore...')
      const restored = this.restoreOnboardingState()
      if (!restored) {
        console.warn('[StepSummary] Could not restore onboarding state')
      }
    }

    // Critical: Sync onboarding store with backend to ensure no connected pages are missing.
    // Pages connected during onboarding are saved to backend (newPage API) but may be missing
    // from the Vuex store if the user navigated between steps or the store was partially lost.
    await this.syncPagesFromBackend()

    console.log('[StepSummary] Total pages:', this.totalConnectedPages)
    console.log('[StepSummary] All pages:', this.allPages)

    // Update onboarding pages with fresh data (story_config for native status)
    const freshPages = this.$store.state.selectPage.allPages
    Object.keys(this.connectedPages).forEach(network => {
      const networkPages = this.connectedPages[network] || []
      networkPages.forEach(page => {
        const freshPage = freshPages[page.id]
        if (freshPage) {
          this.$store.commit('onboarding/UPDATE_PAGE', {
            network,
            pageId: page.id,
            updates: {
              story_config: freshPage.story_config
            }
          })
        }
      })
    })

    console.log('[StepSummary] Onboarding pages updated with fresh data')
  }
}
</script>

<style lang="scss" scoped>
@import '../variables.scss';

/* Quota warning */
.onboarding-quota-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 24px;
  color: #92400e;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 4px;
    font-weight: 600;
  }

  p {
    margin: 4px 0 0 0;
    font-size: 14px;
    line-height: 1.5;
    display: inline;

    & + p {
      display: block;
      margin-top: 8px;
    }
  }

  .quota-info {
    margin-top: 8px;
    font-size: 13px;
    opacity: 0.85;
    display: block;
  }
}

.onboarding-summary {
  display: flex;
  align-items: center;
  min-height: 100vh;
}

.onboarding-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 64px;
  overflow-y: auto;
}

/* Header section */
.onboarding-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 26px;

  .onboarding-header-content {
    flex: 1;
  }

  .onboarding-header-title {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 12px 0;
  }

  .onboarding-header-subtitle {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
  }
}

/* Search bar */
.onboarding-search-bar {
  position: relative;
  margin-bottom: 24px;
  max-width: 400px;

  .search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    opacity: 0.4;
  }

  .search-input {
    width: 100%;
    padding: 12px 16px 12px 44px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    color: #111827;
    
    &::placeholder {
      color: #9ca3af;
    }

    &:focus {
      outline: none;
      border-color: $primary-color;
    }
  }
}

/* Table */
.onboarding-table-container {
  display: flex;
  flex-direction: column;
  height: 500px;
  background: #ffffff;
  border-radius: 12px;
  overflow: auto;
  margin-bottom: 24px;
}

.onboarding-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f9fafb;
    position: sticky;
    top: 0;
    z-index: 10;

    tr {
      th {
        padding: 12px 16px;
        text-align: left;
        font-size: 13px;
        font-weight: 600;
        color: #6b7280;
        cursor: pointer;
        user-select: none;

        &:hover {
          background: #f3f4f6;
        }

        &.col-checkbox {
          width: 40px;
          cursor: default;
          
          &:hover {
            background: transparent;
          }
        }

        &.col-avatar {
          width: 50px;
          cursor: default;
          
          &:hover {
            background: transparent;
          }
        }

        &.col-name {
          min-width: 200px;
        }

        &.col-network {
          min-width: 150px;
        }

        &.col-group {
          min-width: 120px;
        }

        &.col-status {
          min-width: 150px;
        }

        &.col-options {
          width: 60px;
          cursor: default;
          
          &:hover {
            background: transparent;
          }
        }

        .status-header-icon {
          width: 16px;
          height: 16px;
          vertical-align: middle;
          margin-right: 6px;
          opacity: 0.7;
        }

        .sort-indicator {
          margin-left: 6px;
          font-size: 10px;
        }
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid $bg-ui;
      transition: background-color 0.15s;

      &:hover {
        background: #f9fafb;
      }

      &:last-child {
        border-bottom: none;

      }

      td {
        padding: 12px 16px;
        font-size: 14px;
        color: #111827;
        vertical-align: middle;

        &.col-checkbox {
          input[type="checkbox"] {
            cursor: pointer;
          }
        }

        &.col-avatar {
          .page-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            object-fit: cover;
          }

          .avatar-placeholder {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background-color: $primary-color;
            color: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 14px;
          }
        }

        &.col-name {
          font-weight: 500;
        }

        &.col-network {
          .network-badge {
            display: flex;
            align-items: center;
            gap: 8px;

            .network-icon {
              width: 20px;
              height: 20px;
              border-radius: 4px;
            }
          }
        }

        &.col-group {
          .group-empty {
            color: #9ca3af;
          }
        }

        &.col-status {
          .status-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            // padding: 4px 12px;
            border-radius: 16px;
            font-size: 13px;
            font-weight: 500;

            .status-dot {
              width: 6px;
              height: 6px;
              border-radius: 50%;
            }

            &.connected {
              // background: #d1fae5;
              color: #065f46;

              .status-dot {
                background: #10b981;
              }
            }

            &.not-connected {
              // background: #fee2e2;
              color: #991b1b;

              .status-dot {
                background: #ef4444;
              }

              &.is-clickable {
                cursor: pointer;
                text-decoration: none;
                transition: color 0.15s, opacity 0.15s;

                &:hover {
                  color: #dc2626;
                  opacity: 0.85;
                  text-decoration: none;
                }
              }
            }

            &.not-desired {
              color: #6b7280;

              .status-dot {
                background: #9ca3af;
              }
            }
          }
        }

        &.col-options {
          .options-btn {
            background: transparent;
            border: none;
            cursor: pointer;
            padding: 4px;
            border-radius: 4px;
            transition: background 0.15s;

            &:hover {
              background: #e5e7eb;
            }
          }
        }

        &.col-actions {
          width: 60px;
          text-align: center;

          .delete-btn {
            background: transparent;
            border: none;
            cursor: pointer;
            padding: 6px;
            border-radius: 4px;
            transition: background 0.15s, color 0.15s;
            color: #6b7280;
            display: inline-flex;
            align-items: center;
            justify-content: center;

            &:hover {
              background: #fee2e2;
              color: #dc2626;
            }

            svg {
              stroke: currentColor;
            }
          }
        }
      }
    }
  }
}

.empty-state {
  padding: 64px 32px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

/* Footer section */
.onboarding-footer-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: auto;
  padding-top: 24px;
}

.onboarding-footer {
  display: flex;
  align-items: center;
  gap: 12px;

  .onboarding-btn-primary {
    padding: 12px 32px;
    background: $primary-color;
    color: #ffffff;
    border: none;
    border-radius: 20px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover:not(:disabled) {
      background: darken($primary-color, 8%);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

/* Checkbox styling */
input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: $primary-color;
}

@media (max-width: 1200px) {
  .onboarding-content {
    padding: 32px 24px;
  }

  .onboarding-header-title {
    font-size: 24px;
  }

  .onboarding-table-container {
    overflow-x: auto;
  }

  .onboarding-table {
    min-width: 900px;
  }
}
</style>
